/**
 * ⭐⭐ `R2-UI19` · **LA DECLARACIÓN QUE PUBLICA `admin-web`, LEÍDA POR LA CONSOLA.**
 * `// ADR-0384 E` · enmienda `ADR-0353` §② opción (a) · `PRD_R2.md:154`, reglas 14-15.
 *
 * Es la capa de lectura de la declaración del lado de la SPA para UNA pregunta:
 * **¿en qué brazo cae el Acompañante?** La regla, textual de `ADR-0384` E:
 *
 *   > «La consola pide `GET /configuracion` a `admin-web` … y toma **el único brazo
 *   > declarado con `derivacionHabilitada: true`** … Si hay cero o más de uno, el
 *   > Acompañante no cae en ningún brazo: ve la pantalla de acceso denegado con un
 *   > texto constante, y **nunca** hay un brazo de repuesto escrito en el código.»
 *
 * ⛔ **La fuente es `GET /configuracion` de `admin-web`, no `configuracion.json`**
 * (guard ② de `r2-ui19.md`): el `configuracion.json` de la SPA es un archivo del
 * despliegue sin contraste (`configuracion.ts`: «nada verifica que coincidan con los
 * de verdad»). Y tampoco `ARM_VALUES`: elegir por posición es inferir el brazo del
 * nombre con un paso más.
 *
 * ⛔ **Ningún literal de brazo acá** (guard ①), tampoco como repuesto: cualquier
 * falla —red, un 503 porque `admin-web` todavía no resolvió su configuración, un
 * cuerpo sin `brazos`, cero o dos brazos con derivación— da `null`, y `null` es
 * «acceso denegado», no un brazo.
 *
 * ⚠️ **La autorización sigue siendo del servidor** (guard ③): esto sólo decide dónde
 * CAE el Acompañante. Si la declaración y el servidor difieren, ve un 403 visible.
 *
 * La forma del cuerpo, medida en `apps/bot-web/src/compartido/instancia.ts`
 * (`cuerpoDeConfiguracion`, que `admin-web` sirve en `GET /configuracion` sin
 * sesión): `{ instancia: {…}, brazos: [{ id, nombre, derivacionHabilitada }], … }`.
 */

import { useEffect, useRef, useState } from "react"

/** Un brazo tal como lo publica `GET /configuracion` (sólo lo que esta consola lee). */
export interface BrazoPublicado {
  readonly id: string
  readonly derivacionHabilitada: boolean
}

/** El segmento de `admin-web`. Es el mismo `SEGMENTO_DE_CONFIGURACION` del servidor. */
const SEGMENTO_DE_LA_DECLARACION = "configuracion"

/**
 * Los brazos del cuerpo, o `null` si el cuerpo no tiene la forma publicada. Un brazo
 * mal formado invalida la lista entera: contar «uno con derivación» sobre una lista a
 * medio leer sería decidir con datos que no se leyeron.
 */
export function brazosPublicados(cuerpo: unknown): readonly BrazoPublicado[] | null {
  if (cuerpo === null || typeof cuerpo !== "object") return null
  const brazos = (cuerpo as { readonly brazos?: unknown }).brazos
  if (!Array.isArray(brazos)) return null
  const leidos: BrazoPublicado[] = []
  for (const brazo of brazos as readonly unknown[]) {
    if (brazo === null || typeof brazo !== "object") return null
    const { id, derivacionHabilitada } = brazo as { readonly id?: unknown; readonly derivacionHabilitada?: unknown }
    if (typeof id !== "string" || id === "" || typeof derivacionHabilitada !== "boolean") return null
    leidos.push({ id, derivacionHabilitada })
  }
  return leidos
}

/**
 * ⭐ **La regla de `ADR-0384` E, entera.** El único brazo con derivación habilitada;
 * con cero o con más de uno, `null` (acceso denegado). V4 garantiza exactamente uno
 * en un ensayo, pero la consola no lo presupone: lo cuenta.
 */
export function brazoConDerivacion(brazos: readonly BrazoPublicado[]): string | null {
  const conDerivacion = brazos.filter((b) => b.derivacionHabilitada)
  return conDerivacion.length === 1 ? conDerivacion[0]!.id : null
}

/** Pide la declaración y devuelve el brazo del Acompañante, o `null`. Nunca rechaza. */
export type LectorDelBrazoDelAcompanante = () => Promise<string | null>

export function crearLectorDelBrazoDelAcompanante(adminWebUrl: string): LectorDelBrazoDelAcompanante {
  return async () => {
    try {
      /*
       * ⚠️ `credentials: "include"` como los otros bordes de `admin-web`: la ruta no
       * exige sesión, pero así el pedido viaja por el mismo camino CORS que ya está
       * probado y no abre una variante nueva.
       */
      const respuesta = await fetch(`${adminWebUrl}/${SEGMENTO_DE_LA_DECLARACION}`, { credentials: "include" })
      if (!respuesta.ok) return null
      const brazos = brazosPublicados(await respuesta.json())
      return brazos === null ? null : brazoConDerivacion(brazos)
    } catch {
      return null
    }
  }
}

/**
 * Lo que la guardia sabe del brazo del Acompañante. `pidiendo` NO es `null`: mientras
 * la declaración viaja, el Acompañante ve el estado de carga, no Inicio ni un brazo
 * (`r2-ui19.md` «Momento»).
 */
export type BrazoDelAcompanante =
  | { readonly fase: "sin-pedir" }
  | { readonly fase: "pidiendo" }
  | { readonly fase: "resuelto"; readonly brazo: string | null }

/**
 * Pide la declaración UNA vez, y sólo cuando `hace_falta` se vuelve verdadero (con la
 * sesión abierta de un rol que cae en el brazo con derivación). El Administrador no la
 * pide: cae en Inicio y no depende de esto.
 */
export function useBrazoDelAcompanante(lector: LectorDelBrazoDelAcompanante, haceFalta: boolean): BrazoDelAcompanante {
  const [estado, setEstado] = useState<BrazoDelAcompanante>({ fase: "sin-pedir" })
  /*
   * ⚠️ Dos refs y no una dependencia del estado: si el efecto dependiera de la fase,
   * pasar a `pidiendo` lo desmontaría y la respuesta caería en un componente «muerto».
   * `pedido` sobrevive al doble montaje de `StrictMode` (se pide UNA vez); `montado`
   * vuelve a `true` en el segundo montaje, así que la respuesta se aplica.
   */
  const pedido = useRef(false)
  const montado = useRef(true)
  useEffect(() => {
    montado.current = true
    return () => {
      montado.current = false
    }
  }, [])
  useEffect(() => {
    if (!haceFalta || pedido.current) return
    pedido.current = true
    setEstado({ fase: "pidiendo" })
    void lector().then((brazo) => {
      if (montado.current) setEstado({ fase: "resuelto", brazo })
    })
  }, [haceFalta, lector])
  return estado
}
